// =============================================================================
// sales-appointments.ts — Contrato Zod compartilhado da agenda comercial
// (`contact_appointments`, F33-S01/F33-S02).
//
// Fonte única consumida pela API (validação de request/response,
// modules/sales/appointments) e pelo frontend (F33-S07 e seguintes) — nenhum
// dos dois lados redeclara enums ou o shape de resposta.
//
// Convenção de payload (igual a tasks.ts): camelCase em toda a borda,
// paginação via limit/offset (não page/per_page), datas como string ISO 8601
// (UTC, com offset) ou null.
//
// `isOverdue` é CALCULADO na borda (`scheduledAt < now()` com `status` ainda
// `scheduled`) — NUNCA uma coluna do banco (decisão doc 26 §8, ver
// apps/api/src/db/schema/contactAppointments.ts). Nenhum consumidor deve
// tratar este campo como persistido.
//
// LGPD (doc 17 §8.3/§8.5): `notes` e `reason` são texto livre preenchido por
// humano e podem conter PII do titular. O consumidor HTTP (API) garante
// redact em log — precisa estar NAS DUAS listas: `redact.paths` de app.ts
// (a que o Fastify usa) e REDACT_PATHS de lib/logger.ts — nunca persistir
// em payload de evento (outbox) sem tratamento.
// =============================================================================
import { z } from 'zod';
import { AppointmentStatusSchema, ContactChannelSchema, SALES_OUTCOME_META, SalesOutcomeCodeSchema, } from './sales.js';
// ---------------------------------------------------------------------------
// Helper de querystring — scalar→array.
//
// O parser de querystring do Fastify entrega `string` quando um param aparece
// uma única vez (`?status=scheduled`) e `string[]` quando repete
// (`?status=a&status=b`). `fastify-type-provider-zod` não faz coerção (ao
// contrário do ajv), então um `z.array(...)` cru rejeita o caso de valor
// único com 400. Este preprocess normaliza scalar→array antes da validação
// (mesmo padrão de packages/shared-schemas/src/reports.ts).
// ---------------------------------------------------------------------------
function arrayQueryParam(schema) {
    return z.preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(schema).optional());
}
// ---------------------------------------------------------------------------
// Response — um compromisso da agenda comercial
// ---------------------------------------------------------------------------
export const AppointmentSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    /** Herdada do lead no momento do agendamento — nunca escolhida pelo ator (doc 26 §8). */
    cityId: z.string().uuid(),
    leadId: z.string().uuid(),
    /** Vínculo opcional com a fila de uma campanha comercial. FK real só em F35-S02. */
    campaignMemberId: z.string().uuid().nullable(),
    /** `agents.id` do vendedor responsável — NÃO é `users.id` (agente pode não ter login). */
    assignedAgentId: z.string().uuid(),
    /** `users.id` de quem criou o registro. null = origem automática (worker de campanha). */
    createdByUserId: z.string().uuid().nullable(),
    /** ISO 8601 com offset — sempre armazenado em UTC; fuso aplicado só na apresentação. */
    scheduledAt: z.string(),
    durationMinutes: z.number().int().positive(),
    channel: ContactChannelSchema,
    /** Motivo/contexto curto do agendamento. Texto livre — PII potencial (doc 17 §8.3). */
    reason: z.string().nullable(),
    status: AppointmentStatusSchema,
    /** Desfecho comercial. null enquanto status !== 'completed'. */
    outcomeCode: SalesOutcomeCodeSchema.nullable(),
    /** Observação livre. Texto livre — PII potencial (doc 17 §8.3). */
    notes: z.string().nullable(),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    /** Aponta para o compromisso substituído quando este resulta de um reagendamento. */
    supersedesAppointmentId: z.string().uuid().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    /**
     * CALCULADO na resposta (`scheduledAt < now()` com `status === 'scheduled'`).
     * Nunca lido de uma coluna — não existe `overdue`/`is_late` no banco.
     */
    isOverdue: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// List query — GET /api/sales/appointments
// ---------------------------------------------------------------------------
export const AppointmentListQuerySchema = z.object({
    /** Filtro por responsável (agents.id). Vendedor sem `manage` sempre fica restrito ao próprio. */
    assignedAgentId: arrayQueryParam(z.string().uuid()),
    /**
     * Filtro por lead — usado pelo CRM/detalhe do lead para mostrar sua
     * "próxima ação" (doc 26 §22). COMPÕE com o escopo de cidade e a
     * restrição de agente no repository — nunca os substitui. Um `leadId`
     * fora do escopo do ator devolve lista vazia, nunca erro (F33-S11).
     */
    leadId: arrayQueryParam(z.string().uuid()),
    channel: arrayQueryParam(ContactChannelSchema),
    campaignMemberId: arrayQueryParam(z.string().uuid()),
    /** Situação do compromisso. "Atrasado" não é um valor aqui — é `status=scheduled` + `scheduledAt` no passado. */
    status: arrayQueryParam(AppointmentStatusSchema),
    scheduledFrom: z.string().datetime({ offset: true }).optional(),
    scheduledTo: z.string().datetime({ offset: true }).optional(),
    sortBy: z.enum(['scheduledAt', 'createdAt']).default('scheduledAt'),
    sortOrder: z.enum(['asc', 'desc']).default('asc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ---------------------------------------------------------------------------
// List response
// ---------------------------------------------------------------------------
export const AppointmentListResponseSchema = z.object({
    data: z.array(AppointmentSchema),
    total: z.number().int().describe('Total de compromissos que atendem ao filtro'),
    limit: z.number().int(),
    offset: z.number().int(),
});
// ---------------------------------------------------------------------------
// Create — POST /api/sales/appointments
// ---------------------------------------------------------------------------
export const AppointmentCreateBodySchema = z.object({
    leadId: z.string().uuid(),
    /**
     * Omitir = o próprio agente do ator autenticado. Informar um agente
     * diferente exige `sales_appointments:manage` (verificado no service).
     */
    assignedAgentId: z.string().uuid().optional(),
    /** Vínculo opcional com a fila de uma campanha. Aceito e persistido; FK real só em F35-S02. */
    campaignMemberId: z.string().uuid().optional(),
    scheduledAt: z.string().datetime({ offset: true }),
    /** Minutos. Omitir usa o default conservador de 30 (mesmo default da coluna). */
    durationMinutes: z.number().int().positive().max(1440).optional(),
    channel: ContactChannelSchema,
    reason: z.string().min(1).max(500).optional(),
    notes: z.string().min(1).max(4000).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Patch — PATCH /api/sales/appointments/:id
//
// Apenas campos SEM transição de estado (motivo, observação, canal,
// duração). `status`/`scheduledAt`/`assignedAgentId` mudam de dono
// semântico em F33-S03 (complete/reschedule/cancel) — não editáveis aqui.
// ---------------------------------------------------------------------------
export const AppointmentPatchBodySchema = z
    .object({
    /** null limpa o campo; omitir mantém o valor atual. */
    reason: z.string().max(500).nullable().optional(),
    /** null limpa o campo; omitir mantém o valor atual. */
    notes: z.string().max(4000).nullable().optional(),
    channel: ContactChannelSchema.optional(),
    durationMinutes: z.number().int().positive().max(1440).optional(),
})
    .refine((body) => Object.keys(body).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
});
// ---------------------------------------------------------------------------
// Complete — POST /api/sales/appointments/:id/complete (F33-S03)
//
// Aplica os metadados de SALES_OUTCOME_META (F32-S01, sales.ts):
//   - requiresNote:        `notes` não pode ficar vazio/ausente.
//   - requiresAppointment: `nextAppointment` (data/hora do retorno) é
//     obrigatório — outcome como `callback_requested` não pode concluir sem
//     já marcar o próximo compromisso (doc 26 §4.1 "concluir com desfecho").
// `nextAppointment`, quando criado, NÃO usa `supersedesAppointmentId` — é um
// compromisso novo e independente (o atual já ocorreu e está encerrado por
// `completed`), diferente de reagendamento (que substitui um compromisso
// que ainda NÃO ocorreu).
// ---------------------------------------------------------------------------
const AppointmentNextAppointmentSchema = z.object({
    scheduledAt: z.string().datetime({ offset: true }),
    /** Omitir usa o mesmo default conservador de 30 minutos. */
    durationMinutes: z.number().int().positive().max(1440).optional(),
    /** Omitir herda o canal do compromisso concluído. */
    channel: ContactChannelSchema.optional(),
    reason: z.string().min(1).max(500).optional(),
});
export const AppointmentCompleteBodySchema = z
    .object({
    outcomeCode: SalesOutcomeCodeSchema,
    /**
     * Observação de conclusão. Texto livre — PII potencial (doc 17 §8.3).
     * Obrigatória quando `SALES_OUTCOME_META[outcomeCode].requiresNote`.
     */
    notes: z.string().min(1).max(4000).optional(),
    /** Retorno agendado no mesmo fluxo. Obrigatório quando `requiresAppointment`. */
    nextAppointment: AppointmentNextAppointmentSchema.optional(),
})
    .superRefine((body, ctx) => {
    const meta = SALES_OUTCOME_META[body.outcomeCode];
    if (meta.requiresNote && (body.notes === undefined || body.notes.trim().length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['notes'],
            message: `O desfecho "${meta.label}" exige observação (notes) para concluir o compromisso.`,
        });
    }
    if (meta.requiresAppointment && body.nextAppointment === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nextAppointment'],
            message: `O desfecho "${meta.label}" exige agendar um retorno (nextAppointment.scheduledAt) para concluir o compromisso.`,
        });
    }
});
export const AppointmentCompleteResponseSchema = z.object({
    /** Compromisso concluído (status='completed'). */
    appointment: AppointmentSchema,
    /** Retorno criado no mesmo fluxo quando o outcome exige (`requiresAppointment`). null caso contrário. */
    nextAppointment: AppointmentSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Reschedule — POST /api/sales/appointments/:id/reschedule (F33-S03)
//
// NÃO edita `scheduledAt` do compromisso existente: cria um sucessor
// apontando para o anterior via `supersedesAppointmentId` e encerra o
// anterior (`status='cancelled'`) na mesma transação (doc 26 §4.1/§22
// "reagendamento preserva autoria e histórico").
//
// `leadId`, `cityId`, `campaignMemberId`, `assignedAgentId` e
// `createdByUserId` são SEMPRE herdados do compromisso substituído — este
// endpoint não reatribui responsável (isso é uma ação de RE-ATRIBUIÇÃO
// distinta, fora de escopo). `channel`/`durationMinutes`/`reason` herdam
// quando omitidos; `notes` NUNCA é herdado automaticamente (minimização
// LGPD — um texto livre potencialmente sensível não deve se propagar por
// inércia entre registros; precisa ser reafirmado explicitamente).
// ---------------------------------------------------------------------------
export const AppointmentRescheduleBodySchema = z.object({
    scheduledAt: z.string().datetime({ offset: true }),
    /** Omitir herda a duração do compromisso substituído. */
    durationMinutes: z.number().int().positive().max(1440).optional(),
    /** Omitir herda o canal do compromisso substituído. */
    channel: ContactChannelSchema.optional(),
    /** Omitir herda o motivo do compromisso substituído. */
    reason: z.string().min(1).max(500).optional(),
    /** Observação do NOVO compromisso — nunca herdada automaticamente. */
    notes: z.string().min(1).max(4000).optional(),
});
export const AppointmentRescheduleResponseSchema = z.object({
    /** Compromisso sucessor — novo, `status='scheduled'`. */
    appointment: AppointmentSchema,
    /** Compromisso substituído — encerrado, `status='cancelled'`. */
    previousAppointment: AppointmentSchema,
});
// ---------------------------------------------------------------------------
// Cancel — POST /api/sales/appointments/:id/cancel (F33-S03)
//
// `reason` (motivo do cancelamento) é gravado na coluna `notes` — não em
// `reason` — porque `contact_appointments.reason` documenta "por que foi
// AGENDADO" (ver contactAppointments.ts), não "por que foi CANCELADO".
// Sobrescrever `reason` perderia o contexto original do agendamento no
// único registro terminal que resta dele. `notes` é o campo de observação
// livre já usado com a mesma semântica por `SALES_OUTCOME_META.requiresNote`
// (sales.ts) — motivo é justamente esse tipo de observação. Se `notes` já
// tiver conteúdo, o motivo do cancelamento é ANEXADO (não sobrescreve) —
// ver cancelAppointmentService em service.ts.
// ---------------------------------------------------------------------------
export const AppointmentCancelBodySchema = z.object({
    reason: z.string().min(1).max(2000),
});
//# sourceMappingURL=sales-appointments.js.map